import { initializeApp } from "firebase/app";
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut, type User } from "firebase/auth";
import Chart from "chart.js/auto";
import "chartjs-adapter-date-fns";
import zoomPlugin from "chartjs-plugin-zoom";

Chart.register(zoomPlugin);

// ---------------------------------------------------------------------------
// Custom plugin: alternating day bands for time-series charts
// ---------------------------------------------------------------------------

const dayBandsPlugin = {
  id: "dayBands",
  beforeDraw(chart: Chart) {
    const xScale = chart.scales.x;
    if (!xScale || xScale.type !== "time") return;

    const ctx = chart.ctx;
    const { top, bottom } = chart.chartArea;
    const minMs = xScale.min;
    const maxMs = xScale.max;

    // Find the start of the first day (midnight UTC)
    const startDate = new Date(minMs);
    startDate.setUTCHours(0, 0, 0, 0);

    const DAY_MS = 86400000;
    let dayStart = startDate.getTime();
    let bandIndex = Math.floor(dayStart / DAY_MS);

    ctx.save();
    while (dayStart < maxMs) {
      const dayEnd = dayStart + DAY_MS;
      if (dayEnd > minMs) {
        const x1 = Math.max(xScale.getPixelForValue(dayStart), chart.chartArea.left);
        const x2 = Math.min(xScale.getPixelForValue(dayEnd), chart.chartArea.right);
        if (x2 > x1 && bandIndex % 2 === 0) {
          ctx.fillStyle = "rgba(100, 116, 139, 0.06)";
          ctx.fillRect(x1, top, x2 - x1, bottom - top);
        }
      }
      dayStart = dayEnd;
      bandIndex++;
    }
    ctx.restore();
  },
};
Chart.register(dayBandsPlugin);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TelemetryReading {
  timestamp: string;
  apower: number;
  voltage: number;
  current: number;
  energy_total: number;
  temp_c: number;
}

interface ApiResponse {
  deviceId: string;
  hours: number;
  count: number;
  data: TelemetryReading[];
}

interface PuckReading {
  timestamp: string;
  channel: number;
  reading: number;
  minReading: number;
  maxReading: number;
  minMaxMean: number;
  maxMean: number;
  minMean: number;
}

interface PucksApiResponse {
  deviceId: string;
  deviceName: string;
  hours: number;
  count: number;
  data: PuckReading[];
}

interface BedPeriod {
  start: string;
  end: string;
  inBed: boolean;
  durationSeconds: number;
}

interface BedTimelinePoint {
  timestamp: string;
  inBed: boolean;
}

interface PilotPresence {
  name: string;
  deviceId: string;
  totalInBed: number;
  totalOutOfBed: number;
  nightWakeups: number;
  prevTotalInBed: number;
  sleepChangePercent: number | null;
  readingCount: number;
  periods: BedPeriod[];
  timeline: BedTimelinePoint[];
}

interface BedPresenceResponse {
  hours: number;
  pilots: PilotPresence[];
}

interface TimePoint {
  x: number;
  y: number;
}

// ---------------------------------------------------------------------------
// Firebase config
// ---------------------------------------------------------------------------

const firebaseConfig = {
  apiKey: "AIzaSyC6rWNeLXtlHO8rnZ2rsI8Bpmgnw8ui1us",
  authDomain: "stc-production-150269.firebaseapp.com",
  projectId: "stc-production-150269",
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FUNCTION_URL =
  "https://europe-west2-stc-production-150269.cloudfunctions.net/firehose-query";
const PUCKS_FUNCTION_URL =
  "https://europe-west2-stc-production-150269.cloudfunctions.net/calibrated-pucks-query";
const BED_PRESENCE_URL =
  "https://europe-west2-stc-production-150269.cloudfunctions.net/bed-presence-query";
const API_KEY = "UlT_MNOgTnCqUhw5fd6hTbrfSkRVewkEkpLYwQ04HqU";

const DEVICES: readonly string[] = [
  "shl_048308de4a40", "shl_7cc6b6972d52", "shl_94b21608e522",
  "shl_b0c7dec4ebc2", "shl_048308dd8c10", "shl_040d84e84997",
  "shl_e8e07ec5d201", "shl_048308ddd748", "shl_b0c7de792a0a",
  "shl_040d84e84b02", "shl_b0c7de7bd1fe", "shl_38398f827089",
  "shl_3c2ef56f6dfa", "shl_94b216085094", "shl_286847efb889",
  "shl_b0c7de7c7a2e", "shl_7cc6b6929569", "shl_7cc6b69f0da0",
  "shl_048308dd84a0", "shl_3c8a1fec8368", "shl_048308dd7e94",
  "shl_94b21602d04f", "shl_7cc6b69f2761", "shl_7cc6b68053d5",
  "shl_7cc6b67f87c5", "shl_0cae5f62b860", "shl_38398fa12eb0",
  "shl_b0c7de6cb925", "shl_cc7b5c8a4a7c", "shl_048308dd9528",
  "shl_0cae5f5d1a6d", "shl_7cc6b6164ab9", "shl_b0c7de782e15",
  "shl_b0c7dec7572a", "shl_048308de6784", "shl_94b2160a124c",
];

const PUCK_DEVICES: { id: string; name: string }[] = [
  { id: "stc_c8fbae38a60e", name: "AB v3" },
  { id: "stc_c8fbae385840", name: "NT Bed" },
  { id: "stc_c8fbae381ecc", name: "MH Bed" },
  { id: "stc_c8fbae481f04", name: "Dylan" },
  { id: "stc_c8fbae48444e", name: "Pilot19" },
];

const CHANNEL_COLORS = ["#60a5fa", "#34d399", "#f87171", "#f59e0b", "#a78bfa", "#fb923c", "#2dd4bf", "#e879f9"];

// ---------------------------------------------------------------------------
// DOM elements
// ---------------------------------------------------------------------------

const loginScreen = document.getElementById("loginScreen")!;
const dashboardScreen = document.getElementById("dashboardScreen")!;
const loginForm = document.getElementById("loginForm") as HTMLFormElement;
const emailInput = document.getElementById("emailInput") as HTMLInputElement;
const passwordInput = document.getElementById("passwordInput") as HTMLInputElement;
const loginError = document.getElementById("loginError")!;
const loginBtn = document.getElementById("loginBtn") as HTMLButtonElement;
const logoutBtn = document.getElementById("logoutBtn") as HTMLButtonElement;

// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------

loginForm.addEventListener("submit", async (e: Event) => {
  e.preventDefault();
  loginBtn.disabled = true;
  loginError.textContent = "";

  try {
    await signInWithEmailAndPassword(auth, emailInput.value, passwordInput.value);
  } catch (err) {
    loginError.textContent = err instanceof Error ? err.message : "Login failed";
    loginBtn.disabled = false;
  }
});

logoutBtn.addEventListener("click", () => {
  signOut(auth);
});

// ---------------------------------------------------------------------------
// Sidebar tab switching
// ---------------------------------------------------------------------------

const sidebarTabs = document.querySelectorAll<HTMLElement>(".sidebar-tab");
const tabViews = document.querySelectorAll<HTMLElement>(".tab-view");

sidebarTabs.forEach((tab) => {
  tab.addEventListener("click", () => {
    const target = tab.dataset.tab;
    if (!target) return;

    sidebarTabs.forEach((t) => t.classList.remove("active"));
    tab.classList.add("active");

    tabViews.forEach((v) => {
      v.classList.toggle("active", v.id === `${target}View`);
    });
  });
});

// ---------------------------------------------------------------------------
// Chart setup — shared defaults
// ---------------------------------------------------------------------------

const chartDefaults = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index" as const, intersect: false },
  plugins: { legend: { display: false } },
  scales: {
    x: {
      type: "time" as const,
      time: { tooltipFormat: "dd MMM HH:mm" },
      grid: { color: "#1e293b" },
      ticks: { color: "#64748b", maxTicksLimit: 12 },
    },
    y: {
      grid: { color: "#334155" },
      ticks: { color: "#64748b" },
    },
  },
};

function makeChart(id: string, label: string, color: string, unit: string): Chart<"line"> {
  const canvas = document.getElementById(id) as HTMLCanvasElement | null;
  if (!canvas) {
    throw new Error(`Canvas element #${id} not found`);
  }

  return new Chart(canvas, {
    type: "line",
    data: {
      datasets: [{
        label,
        borderColor: color,
        backgroundColor: color + "20",
        fill: true,
        pointRadius: 0,
        borderWidth: 1.5,
        tension: 0.3,
        data: [] as TimePoint[],
      }],
    },
    options: {
      ...chartDefaults,
      plugins: {
        ...chartDefaults.plugins,
        title: {
          display: true,
          text: label,
          color: "#94a3b8",
          align: "start",
          font: { size: 13 },
        },
      },
      scales: {
        ...chartDefaults.scales,
        y: {
          ...chartDefaults.scales.y,
          title: { display: true, text: unit, color: "#64748b" },
        },
      },
    },
  });
}

// ---------------------------------------------------------------------------
// Power charts — deferred until after login
// ---------------------------------------------------------------------------

let powerChart: Chart<"line">;
let voltageChart: Chart<"line">;
let currentChart: Chart<"line">;
let tempChart: Chart<"line">;
let chartsInitialised = false;

function initCharts(): void {
  if (chartsInitialised) return;
  powerChart = makeChart("powerChart", "Active Power", "#f59e0b", "W");
  voltageChart = makeChart("voltageChart", "Voltage", "#60a5fa", "V");
  currentChart = makeChart("currentChart", "Current", "#34d399", "A");
  tempChart = makeChart("tempChart", "Device Temperature", "#f87171", "°C");
  chartsInitialised = true;
}

// ---------------------------------------------------------------------------
// Device dropdown (Power)
// ---------------------------------------------------------------------------

const deviceSelect = document.getElementById("deviceSelect") as HTMLSelectElement;
for (const d of DEVICES) {
  const opt = document.createElement("option");
  opt.value = d;
  opt.textContent = d;
  deviceSelect.appendChild(opt);
}

// ---------------------------------------------------------------------------
// Device dropdown (Pucks)
// ---------------------------------------------------------------------------

const pucksDeviceSelect = document.getElementById("pucksDeviceSelect") as HTMLSelectElement;
for (const d of PUCK_DEVICES) {
  const opt = document.createElement("option");
  opt.value = d.id;
  opt.textContent = `${d.name} (${d.id})`;
  pucksDeviceSelect.appendChild(opt);
}

// ---------------------------------------------------------------------------
// Power data loading
// ---------------------------------------------------------------------------

function average(values: number[]): number {
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function setText(id: string, text: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = text;
}

async function loadData(): Promise<void> {
  const deviceId = deviceSelect.value;
  const hoursSelect = document.getElementById("hoursSelect") as HTMLSelectElement;
  const hours = hoursSelect.value;
  const btn = document.getElementById("loadBtn") as HTMLButtonElement;
  const statusEl = document.getElementById("status")!;

  btn.disabled = true;
  statusEl.textContent = "Querying BigQuery\u2026";

  try {
    const url = `${FUNCTION_URL}?deviceId=${deviceId}&hours=${hours}`;
    const resp = await fetch(url, { headers: { "X-API-Key": API_KEY } });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

    const result = (await resp.json()) as ApiResponse;

    const toPoints = (extract: (d: TelemetryReading) => number): TimePoint[] =>
      result.data.map((d) => ({ x: new Date(d.timestamp).getTime(), y: extract(d) }));

    powerChart.data.datasets[0].data = toPoints((d) => d.apower);
    voltageChart.data.datasets[0].data = toPoints((d) => d.voltage);
    currentChart.data.datasets[0].data = toPoints((d) => d.current);
    tempChart.data.datasets[0].data = toPoints((d) => d.temp_c);

    [powerChart, voltageChart, currentChart, tempChart].forEach((c) => c.update());

    if (result.data.length > 0) {
      setText("avgPower", average(result.data.map((d) => d.apower)).toFixed(1) + " W");
      setText("avgVoltage", average(result.data.map((d) => d.voltage)).toFixed(1) + " V");
      setText("avgCurrent", (average(result.data.map((d) => d.current)) * 1000).toFixed(0) + " mA");
      setText("avgTemp", average(result.data.map((d) => d.temp_c)).toFixed(1) + " \u00b0C");
      setText("energyTotal", result.data[result.data.length - 1].energy_total.toFixed(1) + " Wh");

      const statsEl = document.getElementById("stats");
      if (statsEl) statsEl.style.display = "grid";
    }

    statusEl.textContent = `${result.count.toLocaleString()} data points loaded`;
  } catch (err) {
    statusEl.textContent = `Error: ${err instanceof Error ? err.message : String(err)}`;
  } finally {
    btn.disabled = false;
  }
}

// ---------------------------------------------------------------------------
// Pucks charts
// ---------------------------------------------------------------------------

let pucksOverviewChart: Chart<"line"> | null = null;
const pucksChannelCharts: Map<number, Chart<"line">> = new Map();

// Zoom/pan config for pucks charts
// Ctrl+scroll (or trackpad pinch, which fires ctrlKey=true) to zoom,
// plain two-finger scroll to pan left/right
const zoomPluginConfig = {
  zoom: {
    wheel: { enabled: true, modifierKey: "ctrl" as const },
    pinch: { enabled: true },
    mode: "x" as const,
  },
  pan: {
    enabled: true,
    mode: "x" as const,
  },
};

// Decimation config — LTTB thins to ~500 points while preserving visual shape
const decimationConfig = {
  enabled: true,
  algorithm: "lttb" as const,
  samples: 500,
};

// Shared x-axis config for pucks charts — multi-level time labels with day context
const pucksXScale = {
  type: "time" as const,
  time: {
    tooltipFormat: "EEEE dd MMM yyyy, HH:mm:ss",
    displayFormats: {
      minute: "HH:mm",
      hour: "HH:mm",
      day: "EEE dd MMM",
    },
  },
  grid: { color: "#1e293b" },
  ticks: {
    color: "#64748b",
    maxTicksLimit: 14,
    major: { enabled: true },
    font: (ctx: { tick?: { major?: boolean } }) =>
      ctx.tick?.major ? { weight: "bold" as const, size: 12 } : { size: 11 },
    callback: function (this: { getLabelForValue(v: number): string }, value: string | number) {
      const ms = typeof value === "number" ? value : Number(value);
      const d = new Date(ms);
      const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
      const date = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
      return [time, date];
    },
  },
};

function initPucksOverviewChart(): void {
  if (pucksOverviewChart) return;
  const canvas = document.getElementById("pucksOverviewChart") as HTMLCanvasElement;
  pucksOverviewChart = new Chart(canvas, {
    type: "line",
    data: { datasets: [] },
    options: {
      ...chartDefaults,
      parsing: false,
      plugins: {
        legend: { display: true, labels: { color: "#94a3b8", usePointStyle: true, pointStyle: "line" } },
        title: {
          display: true,
          text: "All Channels + Average  (pinch to zoom, scroll to pan)",
          color: "#94a3b8",
          align: "start",
          font: { size: 13 },
        },
        zoom: zoomPluginConfig,
        decimation: decimationConfig,
      },
      scales: {
        x: pucksXScale,
        y: {
          ...chartDefaults.scales.y,
          title: { display: true, text: "Reading", color: "#64748b" },
        },
      },
    },
  });
}

function createChannelChart(channel: number, container: HTMLElement): Chart<"line"> {
  const section = document.createElement("div");
  section.className = "channel-section";
  const heading = document.createElement("h3");
  heading.textContent = `Channel ${channel}`;
  section.appendChild(heading);

  const wrapper = document.createElement("div");
  wrapper.className = "chart-container";
  const canvas = document.createElement("canvas");
  canvas.style.maxHeight = "240px";
  wrapper.appendChild(canvas);
  section.appendChild(wrapper);
  container.appendChild(section);

  return new Chart(canvas, {
    type: "line",
    data: { datasets: [] },
    options: {
      ...chartDefaults,
      parsing: false,
      plugins: {
        legend: { display: true, labels: { color: "#94a3b8", usePointStyle: true, pointStyle: "line" } },
        title: {
          display: true,
          text: `Channel ${channel} Detail`,
          color: "#94a3b8",
          align: "start",
          font: { size: 13 },
        },
        zoom: zoomPluginConfig,
        decimation: decimationConfig,
      },
      scales: {
        x: pucksXScale,
        y: {
          ...chartDefaults.scales.y,
          title: { display: true, text: "Reading", color: "#64748b" },
        },
      },
    },
  });
}

function makeDataset(label: string, color: string, data: TimePoint[], fill = false) {
  return {
    label,
    borderColor: color,
    backgroundColor: fill ? color + "20" : "transparent",
    fill,
    pointRadius: 0,
    borderWidth: 1.5,
    tension: 0.3,
    data,
  };
}

// Track current pucks device so background fetch can bail if device changed
let pucksCurrentDeviceId = "";
let pucksBackgroundAbort: AbortController | null = null;

function renderPucksData(result: PucksApiResponse, createCharts: boolean): { channels: number } {
  initPucksOverviewChart();

  // Group data by channel
  const byChannel = new Map<number, PuckReading[]>();
  for (const d of result.data) {
    const arr = byChannel.get(d.channel) || [];
    arr.push(d);
    byChannel.set(d.channel, arr);
  }

  const sortedChannels = [...byChannel.keys()].sort((a, b) => a - b);

  // Compute per-timestamp average across all channels
  const timestampReadings = new Map<string, number[]>();
  for (const d of result.data) {
    const arr = timestampReadings.get(d.timestamp) || [];
    arr.push(d.reading);
    timestampReadings.set(d.timestamp, arr);
  }

  // Build overview chart datasets: one line per channel + average
  const overviewDatasets = sortedChannels.map((ch, i) => {
    const chData = byChannel.get(ch)!;
    const color = CHANNEL_COLORS[i % CHANNEL_COLORS.length];
    return makeDataset(
      `Ch ${ch}`,
      color,
      chData.map((d) => ({ x: new Date(d.timestamp).getTime(), y: d.reading })),
    );
  });

  // Average line — use timestamps from the first channel as reference
  if (sortedChannels.length > 0) {
    const firstCh = byChannel.get(sortedChannels[0])!;
    const avgData: TimePoint[] = firstCh.map((d) => {
      const readings = timestampReadings.get(d.timestamp) || [d.reading];
      const avg = readings.reduce((s, v) => s + v, 0) / readings.length;
      return { x: new Date(d.timestamp).getTime(), y: avg };
    });
    overviewDatasets.push(makeDataset("Average", "#e2e8f0", avgData));
  }

  pucksOverviewChart!.data.datasets = overviewDatasets;
  pucksOverviewChart!.update();

  const container = document.getElementById("channelCharts")!;

  if (createCharts) {
    // Destroy old channel charts and create fresh ones
    for (const chart of pucksChannelCharts.values()) {
      chart.destroy();
    }
    pucksChannelCharts.clear();
    container.innerHTML = "";

    for (const ch of sortedChannels) {
      const chData = byChannel.get(ch)!;
      const toPoints = (extract: (d: PuckReading) => number): TimePoint[] =>
        chData.map((d) => ({ x: new Date(d.timestamp).getTime(), y: extract(d) }));

      const chart = createChannelChart(ch, container);
      chart.data.datasets = [
        makeDataset("Reading", "#e2e8f0", toPoints((d) => d.reading)),
        makeDataset("MinMaxMean", "#60a5fa", toPoints((d) => d.minMaxMean)),
        makeDataset("MaxMean", "#34d399", toPoints((d) => d.maxMean)),
        makeDataset("MinMean", "#f87171", toPoints((d) => d.minMean)),
      ];
      chart.update();
      pucksChannelCharts.set(ch, chart);
    }
  } else {
    // Update existing charts in-place (background data refresh)
    for (const ch of sortedChannels) {
      const chart = pucksChannelCharts.get(ch);
      if (!chart) continue;
      const chData = byChannel.get(ch)!;
      const toPoints = (extract: (d: PuckReading) => number): TimePoint[] =>
        chData.map((d) => ({ x: new Date(d.timestamp).getTime(), y: extract(d) }));

      chart.data.datasets = [
        makeDataset("Reading", "#e2e8f0", toPoints((d) => d.reading)),
        makeDataset("MinMaxMean", "#60a5fa", toPoints((d) => d.minMaxMean)),
        makeDataset("MaxMean", "#34d399", toPoints((d) => d.maxMean)),
        makeDataset("MinMean", "#f87171", toPoints((d) => d.minMean)),
      ];
      chart.update();
    }
  }

  return { channels: sortedChannels.length };
}

function resetPucksZoom(): void {
  pucksOverviewChart?.resetZoom();
  for (const chart of pucksChannelCharts.values()) {
    chart.resetZoom();
  }
}

async function loadPucksData(): Promise<void> {
  const deviceId = pucksDeviceSelect.value;
  const btn = document.getElementById("pucksLoadBtn") as HTMLButtonElement;
  const statusEl = document.getElementById("pucksStatus")!;

  // Cancel any in-flight background fetch
  if (pucksBackgroundAbort) {
    pucksBackgroundAbort.abort();
    pucksBackgroundAbort = null;
  }

  pucksCurrentDeviceId = deviceId;
  btn.disabled = true;
  statusEl.textContent = "Loading 24h data\u2026";

  try {
    // Phase 1: Load 24h immediately for fast render
    const url24 = `${PUCKS_FUNCTION_URL}?deviceId=${deviceId}&hours=24`;
    const resp24 = await fetch(url24, { headers: { "X-API-Key": API_KEY } });
    if (!resp24.ok) throw new Error(`HTTP ${resp24.status}`);

    const result24 = (await resp24.json()) as PucksApiResponse;
    const { channels } = renderPucksData(result24, true);

    statusEl.textContent = `${result24.count.toLocaleString()} readings (24h) across ${channels} channels (${result24.deviceName}) — loading 7 days\u2026`;
    btn.disabled = false;

    // Phase 2: Background fetch 7 days
    pucksBackgroundAbort = new AbortController();
    const url7d = `${PUCKS_FUNCTION_URL}?deviceId=${deviceId}&hours=168`;
    const resp7d = await fetch(url7d, {
      headers: { "X-API-Key": API_KEY },
      signal: pucksBackgroundAbort.signal,
    });

    if (!resp7d.ok) throw new Error(`HTTP ${resp7d.status}`);
    const result7d = (await resp7d.json()) as PucksApiResponse;

    // Only apply if user hasn't switched devices
    if (pucksCurrentDeviceId === deviceId) {
      renderPucksData(result7d, false);
      statusEl.textContent = `${result7d.count.toLocaleString()} readings (7 days) across ${channels} channels (${result7d.deviceName}) — pinch to zoom, scroll to pan`;
    }
    pucksBackgroundAbort = null;
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") return; // cancelled, ignore
    statusEl.textContent = `Error: ${err instanceof Error ? err.message : String(err)}`;
  } finally {
    btn.disabled = false;
  }
}

// ---------------------------------------------------------------------------
// Bed Presence
// ---------------------------------------------------------------------------

const bedTimelineCharts: Map<string, Chart<"line">> = new Map();

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function createTimelineChart(pilot: PilotPresence, container: HTMLElement): Chart<"line"> {
  const section = document.createElement("div");
  section.className = "pilot-timeline-section";
  const heading = document.createElement("h3");
  heading.textContent = `${pilot.name} — Timeline`;
  section.appendChild(heading);

  const wrapper = document.createElement("div");
  wrapper.className = "chart-container";
  const canvas = document.createElement("canvas");
  canvas.style.maxHeight = "120px";
  wrapper.appendChild(canvas);
  section.appendChild(wrapper);
  container.appendChild(section);

  const data: TimePoint[] = pilot.timeline.map((t) => ({
    x: new Date(t.timestamp).getTime(),
    y: t.inBed ? 1 : 0,
  }));

  return new Chart(canvas, {
    type: "line",
    data: {
      datasets: [{
        label: "In Bed",
        borderColor: "#60a5fa",
        backgroundColor: "#60a5fa20",
        fill: true,
        pointRadius: 0,
        borderWidth: 1.5,
        stepped: "before",
        data,
      }],
    },
    options: {
      ...chartDefaults,
      plugins: {
        legend: { display: false },
        title: { display: false },
        zoom: zoomPluginConfig,
      },
      scales: {
        ...chartDefaults.scales,
        x: {
          type: "time" as const,
          time: {
            tooltipFormat: "EEE dd MMM HH:mm",
            displayFormats: {
              hour: "EEE HH:mm",
              day: "EEE dd MMM",
            },
          },
          grid: { color: "#1e293b" },
          ticks: {
            color: "#64748b",
            maxTicksLimit: 12,
            major: { enabled: true },
          },
        },
        y: {
          ...chartDefaults.scales.y,
          min: -0.1,
          max: 1.1,
          ticks: {
            color: "#64748b",
            stepSize: 1,
            callback: (value: string | number) => (Number(value) === 1 ? "In Bed" : Number(value) === 0 ? "Out" : ""),
          },
        },
      },
    },
  });
}

async function loadBedPresence(): Promise<void> {
  const hoursSelect = document.getElementById("bedHoursSelect") as HTMLSelectElement;
  const hours = hoursSelect.value;
  const btn = document.getElementById("bedLoadBtn") as HTMLButtonElement;
  const statusEl = document.getElementById("bedStatus")!;

  btn.disabled = true;
  statusEl.textContent = "Querying Firestore\u2026";

  try {
    const url = `${BED_PRESENCE_URL}?hours=${hours}`;
    const resp = await fetch(url, { headers: { "X-API-Key": API_KEY } });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

    const result = (await resp.json()) as BedPresenceResponse;

    // Build pilot summary cards
    const cardsContainer = document.getElementById("pilotCards")!;
    cardsContainer.innerHTML = "";

    for (const pilot of result.pilots) {
      const total = pilot.totalInBed + pilot.totalOutOfBed;
      const inPct = total > 0 ? (pilot.totalInBed / total) * 100 : 0;
      const outPct = 100 - inPct;

      const card = document.createElement("div");
      card.className = "pilot-card";
      const wakeupLabel = pilot.nightWakeups === 1 ? "time" : "times";

      // Sleep change vs previous period
      let changeHtml = "";
      if (pilot.sleepChangePercent !== null) {
        const pct = pilot.sleepChangePercent;
        const arrow = pct > 0 ? "\u25B2" : pct < 0 ? "\u25BC" : "\u25C6";
        const cls = pct > 0 ? "change-up" : pct < 0 ? "change-down" : "change-flat";
        const label = pct > 0 ? `+${pct}%` : `${pct}%`;
        changeHtml = `<span class="sleep-change ${cls}">${arrow} ${label} vs previous period</span>`;
      } else if (pilot.readingCount > 0) {
        changeHtml = `<span class="sleep-change change-flat">No previous data for comparison</span>`;
      }

      card.innerHTML = `
        <h3>${pilot.name}</h3>
        <div class="presence-bar">
          <div class="in-bed" style="width: ${inPct}%"></div>
          <div class="out-of-bed" style="width: ${outPct}%"></div>
        </div>
        <div class="presence-stats">
          <span class="in-bed-stat">In bed: ${formatDuration(pilot.totalInBed)} (${inPct.toFixed(0)}%)</span>
          <span class="out-stat">Out: ${formatDuration(pilot.totalOutOfBed)} (${outPct.toFixed(0)}%)</span>
        </div>
        ${changeHtml}
        <div class="night-wakeups">Up ${pilot.nightWakeups} ${wakeupLabel} between 9pm–9am</div>
        <div class="reading-count">${pilot.readingCount.toLocaleString()} readings &middot; ${pilot.deviceId}</div>
      `;
      cardsContainer.appendChild(card);
    }

    // Build timeline charts
    const timelinesContainer = document.getElementById("pilotTimelines")!;
    for (const chart of bedTimelineCharts.values()) {
      chart.destroy();
    }
    bedTimelineCharts.clear();
    timelinesContainer.innerHTML = "";

    for (const pilot of result.pilots) {
      if (pilot.timeline.length > 0) {
        const chart = createTimelineChart(pilot, timelinesContainer);
        bedTimelineCharts.set(pilot.name, chart);
      }
    }

    const totalReadings = result.pilots.reduce((s, p) => s + p.readingCount, 0);
    statusEl.textContent = `${result.pilots.length} pilots, ${totalReadings.toLocaleString()} total readings`;
  } catch (err) {
    statusEl.textContent = `Error: ${err instanceof Error ? err.message : String(err)}`;
  } finally {
    btn.disabled = false;
  }
}

// ---------------------------------------------------------------------------
// Global exports for onclick handlers
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    loadData: typeof loadData;
    loadPucksData: typeof loadPucksData;
    resetPucksZoom: typeof resetPucksZoom;
    loadBedPresence: typeof loadBedPresence;
  }
}
window.loadData = loadData;
window.loadPucksData = loadPucksData;
window.resetPucksZoom = resetPucksZoom;
window.loadBedPresence = loadBedPresence;

// ---------------------------------------------------------------------------
// Auth state
// ---------------------------------------------------------------------------

function showLogin(): void {
  loginScreen.style.display = "flex";
  dashboardScreen.style.display = "none";
  loginBtn.disabled = false;
  passwordInput.value = "";
  loginError.textContent = "";
}

let autoLoaded = false;

function showDashboard(): void {
  loginScreen.style.display = "none";
  dashboardScreen.style.display = "flex";
  initCharts();

  // Auto-load pucks and bed presence in parallel on first login
  if (!autoLoaded) {
    autoLoaded = true;
    loadPucksData();
    loadBedPresence();
  }
}

onAuthStateChanged(auth, (user: User | null) => {
  if (user) {
    showDashboard();
  } else {
    autoLoaded = false;
    showLogin();
  }
});
